import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material';
import { User, UserRole } from '../../types/user';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { FormikTextField } from '../FormikTextField';
import { FormikDropDown } from '../FormikDropDown';
import { FormikSwitch } from '../FormikSwitch';
import { useGetAMCsQuery } from '../../services';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<User, 'id'> & { changePassword: boolean; amcId?: string }) => void;
  user: User | null;
  amcId?: string;
  loggedInUserIsAdmin: boolean;
}

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Enter a valid email').required('Email is required'),
  password: yup.string().when({
    is: (_: string, context: any) =>
      context?.parent?.isNewUser === true || context?.parent?.changePassword === true,
    then: () =>
      yup
        .string()
        .min(8, 'Password must be at least 8 characters')
        .required('Password is required'),
    otherwise: () => yup.string().optional(),
  }),
  role: yup.string().required('Role is required'),
  isActive: yup.boolean(),
  isNewUser: yup.boolean(),
  amcId: yup.string().when('role', {
    is: (role: string) => role === UserRole.AMC,
    then: (schema) => schema.required('AMC is required for AMC User role'),
    otherwise: (schema) => schema.optional(),
  }),
});

export default function UserForm({
  open,
  onClose,
  onSubmit,
  user,
  amcId,
  loggedInUserIsAdmin = false,
}: UserFormProps) {
  const isCreate = !user;
  const { data: amcs = [] } = useGetAMCsQuery();
  const currentUserRole = useSelector((state: RootState) => state.user.role);

  const initialValues = {
    name: user?.name || '',
    email: user?.email || '',
    isActive: user?.isActive ?? true,
    role: user?.role || '',
    password: '',
    changePassword: false,
    isNewUser: !user,
    amcId: amcId || '',
  };

  let roleOptions = [
    { value: UserRole.ADMIN, label: 'Admin' },
    { value: UserRole.SUPPORT, label: 'Support' },
    { value: UserRole.AMC, label: 'AMC User' },
    { value: UserRole.AMC_ADMIN, label: 'AMC Admin' },
  ];

  // Filter roles for AMC Admin
  if (currentUserRole === UserRole.AMC_ADMIN) {
    roleOptions = roleOptions.filter(
      (option) => option.value === UserRole.AMC || option.value === UserRole.AMC_ADMIN
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{user ? 'Edit User' : 'Add New User'}</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          console.log('Form submitted with values:', values);
          const userData = {
            name: values.name,
            email: values.email,
            isActive: values.isActive,
            role: values.role,
            changePassword: values.changePassword,
            password: values.password,
            amcId: values.amcId || amcId,
          };
          onSubmit(userData);
        }}
      >
        {({ values }) => (
          <Form>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <FormikTextField name="name" label="Full Name" />
                <FormikTextField name="email" label="Email" type="email" disabled={!!user} />
                {(!user || values.changePassword) && (
                  <FormikTextField
                    name="password"
                    label="Password"
                    type="password"
                    helperText="Minimum 8 characters"
                  />
                )}
                {!isCreate && <FormikSwitch label="Change Password" name="changePassword" />}
                <FormikDropDown name="role" label="Role" options={roleOptions} />

                {loggedInUserIsAdmin && !amcId && isCreate && (
                  <FormikDropDown
                    name="amcId"
                    label="AMC"
                    options={amcs.map((a: any) => ({ value: a.id, label: a.name || a.id }))}
                  />
                )}

                {!isCreate && <FormikSwitch name="isActive" label="Active" />}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={onClose} color="inherit">
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {user ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
